import React, { useState } from "react";
import {
  Box,
  Text,
  Flex,
  Button,
  Input,
  Image,
  IconButton,
  SimpleGrid,
  useToast,
} from "@chakra-ui/react";
import { MdDelete, MdAdd, MdPlayCircle, MdStar, MdStarBorder, MdArrowUpward, MdArrowDownward } from "react-icons/md";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";
import PAGE_IDS from "constants/pageIds";
import { GOLD_BORDER, GOLD_TEXT } from "constants/brandColors";
import MediaLightbox from "./MediaLightbox";
import ConfirmDialog from "components/confirm/ConfirmDialog";

const NEWS_PAGE_ID = PAGE_IDS.NEWS;

const byOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0);

// Adds/removes individual images and videos on an existing news item via the
// separate single-file endpoints (POST/PUT/DELETE /newsimage, /newsvideo) —
// kept apart from the multi-file upload done at creation time in
// CreateNews.js. PUT also carries isCover/caption/order (images) or
// caption/order (videos) — added 2026-08-23 alongside the backend support;
// see docs pasted in that session. isCover has no bulk "unset the others"
// call needed client-side — the server does that itself when one image is
// set as cover.
function NewsMediaManager({ newsId, images, videos, onChange }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [lightboxMedia, setLightboxMedia] = useState(null);
  // { kind: "image" | "video", id } of the item pending delete confirmation.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const sortedImages = [...(images || [])].sort(byOrder);
  const sortedVideos = [...(videos || [])].sort(byOrder);

  const putMedia = async (kind, id, updates) => {
    const formData = new FormData();
    formData.append("page_id", NEWS_PAGE_ID);
    formData.append(kind === "image" ? "image_id" : "video_id", id);
    formData.append("news_id", newsId);
    Object.entries(updates).forEach(([key, value]) => formData.append(key, String(value)));
    const response = await fetchWithToken(
      `${process.env.REACT_APP_NEWS_API_URL}/${kind === "image" ? "newsimage" : "newsvideo"}`,
      "PUT",
      formData
    );
    if (!response) throw new Error(`Failed to update ${kind}`);
  };

  const handleSetCover = async (imageId) => {
    try {
      await putMedia("image", imageId, { isCover: true });
      onChange?.();
    } catch (error) {
      toast({ title: t("Failed to set cover image."), description: error.message, status: "error", duration: 5000, isClosable: true, position: "top-right" });
    }
  };

  const handleCaptionBlur = async (kind, item, nextCaption) => {
    if ((item.caption || "") === nextCaption) return;
    try {
      await putMedia(kind, item.id, { caption: nextCaption });
      onChange?.();
    } catch (error) {
      toast({ title: t("Failed to update caption."), description: error.message, status: "error", duration: 5000, isClosable: true, position: "top-right" });
    }
  };

  // Swaps `order` with the neighboring item (direction -1 = up/earlier,
  // +1 = down/later) rather than exposing a raw numeric field — simpler for
  // an admin to click than to keep a whole list's order values consistent.
  const moveItem = async (kind, list, index, direction) => {
    const otherIndex = index + direction;
    if (otherIndex < 0 || otherIndex >= list.length) return;
    const current = list[index];
    const other = list[otherIndex];
    try {
      await Promise.all([
        putMedia(kind, current.id, { order: other.order ?? 0 }),
        putMedia(kind, other.id, { order: current.order ?? 0 }),
      ]);
      onChange?.();
    } catch (error) {
      toast({ title: t("Failed to reorder."), description: error.message, status: "error", duration: 5000, isClosable: true, position: "top-right" });
    }
  };

  const handleAddImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("page_id", NEWS_PAGE_ID);
      formData.append("news_id", newsId);
      formData.append("Image", file);
      // The very first image on an article defaults to cover — otherwise an
      // article can silently end up with no cover image at all (nothing to
      // show as a thumbnail on the site) until someone remembers to set one.
      if (!images || images.length === 0) formData.append("isCover", "true");
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/newsimage`,
        "POST",
        formData
      );
      if (!response) throw new Error("Failed to upload image");
      onChange?.();
      toast({ title: t("Image added."), status: "success", duration: 3000, isClosable: true, position: "top-right" });
    } catch (error) {
      toast({ title: t("Failed to add image."), description: error.message, status: "error", duration: 5000, isClosable: true, position: "top-right" });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAddVideo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingVideo(true);
    try {
      const formData = new FormData();
      formData.append("page_id", NEWS_PAGE_ID);
      formData.append("news_id", newsId);
      formData.append("Video", file);
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/newsvideo`,
        "POST",
        formData
      );
      if (!response) throw new Error("Failed to upload video");
      onChange?.();
      toast({ title: t("Video added."), status: "success", duration: 3000, isClosable: true, position: "top-right" });
    } catch (error) {
      toast({ title: t("Failed to add video."), description: error.message, status: "error", duration: 5000, isClosable: true, position: "top-right" });
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/newsimage`,
        "DELETE",
        { page_id: NEWS_PAGE_ID, image_id: imageId }
      );
      if (!response) throw new Error("Failed to delete image");
      onChange?.();
    } catch (error) {
      toast({ title: t("Failed to delete image."), description: error.message, status: "error", duration: 5000, isClosable: true, position: "top-right" });
    }
  };

  const handleDeleteVideo = async (videoId) => {
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/newsvideo`,
        "DELETE",
        { page_id: NEWS_PAGE_ID, video_id: videoId }
      );
      if (!response) throw new Error("Failed to delete video");
      onChange?.();
    } catch (error) {
      toast({ title: t("Failed to delete video."), description: error.message, status: "error", duration: 5000, isClosable: true, position: "top-right" });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      if (deleteTarget.kind === "image") {
        await handleDeleteImage(deleteTarget.id);
      } else {
        await handleDeleteVideo(deleteTarget.id);
      }
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  return (
    <Box mt="6">
      <Flex justify="space-between" align="center" mb="2">
        <Text fontSize="md" fontWeight="bold">{t("Images")}</Text>
        <Button
          as="label"
          size="xs"
          leftIcon={<MdAdd />}
          variant="outline"
          borderColor={GOLD_BORDER}
          color={GOLD_TEXT}
          isLoading={uploadingImage}
          cursor="pointer"
        >
          {t("Add Image")}
          <Input type="file" accept="image/*" display="none" onChange={handleAddImage} />
        </Button>
      </Flex>
      {sortedImages.length === 0 ? (
        <Text fontSize="sm" color="gray.500" mb="4">{t("No images yet.")}</Text>
      ) : (
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb="4">
          {sortedImages.map((img, index) => (
            <Box key={img.id} borderRadius="md" overflow="hidden" borderWidth="1px" borderColor={img.isCover ? GOLD_BORDER : "gray.200"}>
              <Box position="relative">
                <Image
                  src={img.image_url}
                  alt={img.caption || "news image"}
                  objectFit="cover"
                  h="90px"
                  w="100%"
                  cursor="pointer"
                  _hover={{ opacity: 0.85 }}
                  onClick={() => setLightboxMedia({ type: "image", src: img.image_url, alt: img.caption })}
                />
                <IconButton
                  aria-label={img.isCover ? t("Cover image") : t("Set as cover")}
                  title={img.isCover ? t("Cover image") : t("Set as cover")}
                  icon={img.isCover ? <MdStar /> : <MdStarBorder />}
                  size="xs"
                  bg={img.isCover ? GOLD_BORDER : "whiteAlpha.800"}
                  color={img.isCover ? "#1A1A1A" : "gray.600"}
                  isDisabled={img.isCover}
                  position="absolute"
                  top="2px"
                  left="2px"
                  onClick={() => handleSetCover(img.id)}
                />
                <IconButton
                  aria-label={t("Remove image")}
                  icon={<MdDelete />}
                  size="xs"
                  colorScheme="red"
                  position="absolute"
                  top="2px"
                  right="2px"
                  onClick={() => setDeleteTarget({ kind: "image", id: img.id })}
                />
              </Box>
              <Input
                defaultValue={img.caption || ""}
                key={`${img.id}-${img.caption || ""}`}
                placeholder={t("Caption")}
                size="xs"
                border="none"
                borderTop="1px solid"
                borderColor="gray.200"
                borderRadius="0"
                onBlur={(e) => handleCaptionBlur("image", img, e.target.value)}
              />
              <Flex justify="center" gap={1} py="2px" borderTop="1px solid" borderColor="gray.100">
                <IconButton
                  aria-label={t("Move earlier")}
                  icon={<MdArrowUpward />}
                  size="xs"
                  variant="ghost"
                  isDisabled={index === 0}
                  onClick={() => moveItem("image", sortedImages, index, -1)}
                />
                <IconButton
                  aria-label={t("Move later")}
                  icon={<MdArrowDownward />}
                  size="xs"
                  variant="ghost"
                  isDisabled={index === sortedImages.length - 1}
                  onClick={() => moveItem("image", sortedImages, index, 1)}
                />
              </Flex>
            </Box>
          ))}
        </SimpleGrid>
      )}

      <Flex justify="space-between" align="center" mb="2">
        <Text fontSize="md" fontWeight="bold">{t("Videos")}</Text>
        <Button
          as="label"
          size="xs"
          leftIcon={<MdAdd />}
          variant="outline"
          borderColor={GOLD_BORDER}
          color={GOLD_TEXT}
          isLoading={uploadingVideo}
          cursor="pointer"
        >
          {t("Add Video")}
          <Input type="file" accept="video/*" display="none" onChange={handleAddVideo} />
        </Button>
      </Flex>
      {sortedVideos.length === 0 ? (
        <Text fontSize="sm" color="gray.500">{t("No videos yet.")}</Text>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          {sortedVideos.map((vid, index) => (
            <Box key={vid.id} borderWidth="1px" borderColor="gray.200" borderRadius="md" p="2">
              <Flex align="center" justify="space-between">
                <Flex
                  align="center"
                  gap={2}
                  cursor="pointer"
                  onClick={() => setLightboxMedia({ type: "video", src: vid.video_url, alt: vid.caption })}
                  _hover={{ color: GOLD_TEXT }}
                >
                  <MdPlayCircle />
                  <Text fontSize="sm" noOfLines={1} maxW="140px">
                    {vid.caption || t("Video")}
                  </Text>
                </Flex>
                <Flex gap={1}>
                  <IconButton
                    aria-label={t("Move earlier")}
                    icon={<MdArrowUpward />}
                    size="xs"
                    variant="ghost"
                    isDisabled={index === 0}
                    onClick={() => moveItem("video", sortedVideos, index, -1)}
                  />
                  <IconButton
                    aria-label={t("Move later")}
                    icon={<MdArrowDownward />}
                    size="xs"
                    variant="ghost"
                    isDisabled={index === sortedVideos.length - 1}
                    onClick={() => moveItem("video", sortedVideos, index, 1)}
                  />
                  <IconButton
                    aria-label={t("Remove video")}
                    icon={<MdDelete />}
                    size="xs"
                    colorScheme="red"
                    onClick={() => setDeleteTarget({ kind: "video", id: vid.id })}
                  />
                </Flex>
              </Flex>
              <Input
                defaultValue={vid.caption || ""}
                key={`${vid.id}-${vid.caption || ""}`}
                placeholder={t("Caption")}
                size="xs"
                mt="4px"
                onBlur={(e) => handleCaptionBlur("video", vid, e.target.value)}
              />
            </Box>
          ))}
        </SimpleGrid>
      )}

      <MediaLightbox media={lightboxMedia} onClose={() => setLightboxMedia(null)} />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title={deleteTarget?.kind === "video" ? t("Delete this video?") : t("Delete this image?")}
        body={t("This action cannot be undone.")}
        confirmLabel={t("Delete")}
        isLoading={isDeleting}
        onConfirm={confirmDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </Box>
  );
}

export default NewsMediaManager;
